/* eslint-disable no-shadow, jsx-a11y/anchor-is-valid */

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { t } from 'ttag';

import { Button } from '@components';
import { Membership } from '@modules';
import { mergeClasses } from '@utils';

import getSiteFulfiller from '@utils/getSiteFulfiller';
import NavigationDrawer from './Navigation.Drawer';

import Flyout from '../../assets/Flyout.png';

import { Logo, LogoRGOL, LogoRGOLSmall } from '../../assets';

import Favourites from '../../components/Favourites';
import Flagship from '../../components/Flagship';
import GiftCard from '../../components/GiftCard';

// eslint-disable-next-line import/no-cycle
import { HeaderContext } from '../../Header';

export default function Navigation({
  navigationItems,
  isMobile,
  isMenuOpen,
  setIsMenuOpen
}) {
  const { isLoggedIn } = useContext(HeaderContext);

  const [menuRoute, setMenuRoute] = useState([]);
  const [hoveredLink, setHoveredLink] = useState(null);

  const [USPs, setUSPs] = useState([]);
  const USPType = useMemo(() => 'signup_unisport_fc_-_usp', []);

  const siteFulfiller = useMemo(() => getSiteFulfiller(), []);

  useEffect(() => {
    const fetchUSPs = async () => {
      const response = await fetch(`/api/get_usp/?type=${USPType}`);
      const data = await response.json();

      if (data && data[USPType]) {
        setUSPs(data[USPType].slice(0, 3));
      }
    };

    fetchUSPs();
  }, []);

  const handleBack = useCallback(() => {
    setMenuRoute(prevState => prevState.slice(0, -1));
  }, []);

  /**
   * Adds the baseItemId to the menuRoute.
   * @type {(function(MouseEvent<HTMLAnchorElement>, number): void)}
   */
  const handleNext = useCallback((e, baseItemId) => {
    e?.preventDefault();

    setHoveredLink(null);
    setMenuRoute(prevState => [...prevState, baseItemId]);
  }, []);

  /**
   * Gets the navigationItems based on the menuRoute.
   * @type {function(): Array}
   */
  const getNavigationItems = useCallback(() => {
    let tempData = navigationItems;

    menuRoute.forEach(baseItemId => {
      const nextData = tempData.find(item => item.base_item_id === baseItemId);
      tempData = nextData ? nextData.items : [];
    });

    return tempData?.filter(item => {
      if (item.online) {
        if (item.static_link) return true;

        if (item.items && item.items.length > 0) {
          return item.items.some(child => child.number_of_products > 0);
        }

        if (item.number_of_products > 0) {
          return true;
        }
      }
      return false;
    });
  }, [menuRoute, navigationItems]);

  /**
   * Adds the baseItemId to the menuRoute, if it doesn't exist. Otherwise, removes it.
   * This is used for the root navigationItems.
   * @type {(function(MouseEvent<HTMLAnchorElement>, number): void)}
   */
  const handleClick = useCallback(
    (e, baseItemId) => {
      e?.preventDefault();

      if (menuRoute.includes(baseItemId)) {
        setIsMenuOpen(false);
      } else {
        setIsMenuOpen(true);
        setMenuRoute([baseItemId]);
      }
    },
    [menuRoute, setMenuRoute, setIsMenuOpen]
  );

  /**
   * Renders the Navigation for Desktop based on the navigationItems.
   * @type {(function(Object, boolean): React.ReactNode)}
   */
  const DesktopNavigation = useCallback(
    (navigationItems, isHidden = false) => (
      <nav
        className="box-border hidden h-12 border-0 border-b border-solid border-whisper bg-white px-4 lg:flex"
        aria-hidden={isHidden}
      >
        <ul className="m-0 flex list-none gap-x-2 p-0" role="menu">
          {navigationItems
            ?.filter(({ online }) => online)
            ?.map(({ base_item_id: baseItemId, slug, title }) => (
              <li key={baseItemId} role="none">
                <a
                  className={mergeClasses(
                    'box-border flex h-12 items-center border-0 border-b-4 border-solid border-transparent p-2 text-sm font-semibold text-black outline-none transition-colors duration-150 ease-in-out hover:border-black hover:text-black focus:outline-none focus-visible:border-black xl:text-base',
                    {
                      'border-black': menuRoute.includes(baseItemId),
                      'text-dark-gray':
                        menuRoute?.length > 0 && !menuRoute.includes(baseItemId)
                    }
                  )}
                  href={`/${slug}`}
                  role="menuitem"
                  onClick={e => handleClick(e, baseItemId)}
                >
                  {title}
                </a>
              </li>
            ))}
        </ul>
      </nav>
    ),
    [menuRoute]
  );

  /**
   * Renders the Navigation for Mobile based on the navigationItems.
   * @type {(function(boolean): React.ReactNode)}
   */
  const MobileNavigation = useCallback(
    (isHidden = false) => (
      <div
        className="sticky top-0 box-border flex h-16 items-center justify-between border-0 border-b border-solid border-whisper bg-white px-4 lg:hidden"
        aria-hidden={isHidden}
      >
        <a href="/" className="flex flex-none items-center focus:outline-none">
          {siteFulfiller === 'R-GOL' ? (
            <>
              <LogoRGOL className="hidden h-[24px] shrink-0 lg:block" />
              <LogoRGOLSmall className="block h-[24px] shrink-0 lg:hidden" />
            </>
          ) : (
            <Logo className="h-[24px] w-[118px] shrink-0" />
          )}
        </a>
        <button
          type="button"
          className="m-0 flex items-center border-0 bg-transparent p-3 outline-none focus:outline-none"
          aria-label={t`Close Navigation`}
          onClick={() => setIsMenuOpen(false)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-6 w-6 shrink-0"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>
    ),
    [menuRoute]
  );

  return (
    <>
      <div className="hidden lg:block">
        {DesktopNavigation(navigationItems, isMenuOpen)}
      </div>

      <NavigationDrawer
        menuRoute={menuRoute}
        menuNavigation={
          isMobile ? null : DesktopNavigation(navigationItems, false)
        }
        isOpen={isMenuOpen}
        onClose={() => {
          setIsMenuOpen(false);
        }}
        afterLeave={() => {
          setMenuRoute([]);
        }}
      >
        {isMobile && MobileNavigation(false)}
        <nav className="space-y-8 p-4 lg:mb-16 lg:space-y-8">
          <ul className="m-0 list-none p-0" role="menubar">
            {menuRoute.length > (isMobile ? 0 : 1) && (
              <li role="none">
                <button
                  className="m-0 flex w-full items-center gap-2 border-0 bg-transparent py-2 text-base text-black outline-none transition-colors duration-150 ease-in-out hover:text-black focus:outline-none focus-visible:text-black"
                  type="button"
                  role="menuitem"
                  onClick={() => handleBack()}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4 shrink-0"
                  >
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                  <span className="text-left">{t`Back`}</span>
                </button>
              </li>
            )}

            {getNavigationItems()?.map(
              ({
                base_item_id: baseItemId,
                breakpoint,
                items,
                slug,
                title
              }) => (
                <li key={baseItemId} role="none" className="text-black">
                  <a
                    href={`/${slug}`}
                    className={mergeClasses(
                      'flex items-center justify-between p-2 text-2xl font-bold text-inherit outline-none transition-colors duration-150 ease-in-out focus:outline-none focus-visible:text-black',
                      {
                        'text-dark-gray':
                          hoveredLink && hoveredLink !== baseItemId
                      }
                    )}
                    role="menuitem"
                    onMouseEnter={() => setHoveredLink(baseItemId)}
                    onMouseLeave={() => setHoveredLink(null)}
                    onClick={
                      !breakpoint && items?.length > 0
                        ? e => handleNext(e, baseItemId)
                        : undefined
                    }
                  >
                    <span>{title}</span>
                    {!breakpoint && items?.length > 0 && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-4 w-4 shrink-0"
                      >
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    )}
                  </a>
                </li>
              )
            )}
          </ul>
          {!isLoggedIn && menuRoute.length === (isMobile ? 0 : 1) && (
            <Membership className="max-w-[384px]">
              <Membership.Heading>{t`Become a Member Today`}</Membership.Heading>
              <Membership.USPs>
                {USPs?.map(({ headline }) => (
                  <Membership.USP key={headline}>{headline}</Membership.USP>
                ))}
              </Membership.USPs>
              <div className="flex gap-x-2">
                <Membership.Button as="a" href="/account/login/">
                  {t`Join for Free`}
                </Membership.Button>
                <Membership.Button
                  as="a"
                  href="/account/login/"
                  className="border-black bg-transparent text-black"
                >
                  {t`Login`}
                </Membership.Button>
              </div>
            </Membership>
          )}
          {isLoggedIn && menuRoute.length === (isMobile ? 0 : 1) && (
            <>
              <div className="flex max-w-[256px] gap-x-4">
                <img
                  src={Flyout}
                  alt=""
                  width={80}
                  height={113}
                  className="h-[113px] w-[80px] shrink-0"
                />
                <div className="grow space-y-1">
                  <span className="block text-2xl font-black uppercase leading-none text-black">
                    Locker Room
                  </span>
                  <span className="block text-sm leading-tight text-black">
                    {t`Your Personal Locker Room Universe`}
                  </span>
                  <div className="flex">
                    <Button as="a" href="/account/home/" className="px-1 py-1">
                      <Button.Icon className="h-4 w-4 shrink-0">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M5 12h14" />
                          <path d="m12 5 7 7-7 7" />
                        </svg>
                      </Button.Icon>
                    </Button>
                  </div>
                </div>
              </div>
              <Favourites withText />
            </>
          )}
          {menuRoute.length === (isMobile ? 0 : 1) && (
            <div>
              <a
                href="/faq/"
                className="block p-1 text-black outline-none focus:outline-none"
                aria-label={t`Help and Contact`}
              >
                <span className="text-base">{t`Help and Contact`}</span>
              </a>
              <Flagship />
              <GiftCard />
            </div>
          )}
        </nav>
      </NavigationDrawer>
    </>
  );
}
